import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';

const { FiLayers, FiPlus, FiTrash2 } = FiIcons;

const ModuleTwo = ({ data, onSave, completed }) => {
  const [bookType, setBookType] = useState('nonfiction');
  const [fictionData, setFictionData] = useState({
    act1: '',
    act2: '',
    act3: '',
    act4: '',
    notes: ''
  });
  const [nonfictionChapters, setNonfictionChapters] = useState([
    { chapter: 1, bigIdea: '', biblicalTruth: '', readerShift: '', keyTakeaway: '' }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data.bookType) setBookType(data.bookType);
    if (data.fictionData) setFictionData(data.fictionData);
    if (data.nonfictionChapters) setNonfictionChapters(data.nonfictionChapters);
  }, [data]);

  const handleFictionChange = (field, value) => {
    setFictionData(prev => ({ ...prev, [field]: value }));
  };

  const handleChapterChange = (index, field, value) => {
    setNonfictionChapters(prev => 
      prev.map((chapter, i) => 
        i === index ? { ...chapter, [field]: value } : chapter
      )
    );
  };

  const addChapter = () => {
    setNonfictionChapters(prev => [
      ...prev,
      { 
        chapter: prev.length + 1, 
        bigIdea: '', 
        biblicalTruth: '', 
        readerShift: '', 
        keyTakeaway: '' 
      }
    ]);
  };

  const removeChapter = (index) => {
    if (nonfictionChapters.length > 1) {
      setNonfictionChapters(prev => 
        prev.filter((_, i) => i !== index)
          .map((chapter, i) => ({ ...chapter, chapter: i + 1 }))
      );
    }
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    const dataToSave = {
      bookType,
      fictionData,
      nonfictionChapters
    };
    
    setTimeout(() => {
      onSave(dataToSave, markComplete);
      setIsLoading(false);
    }, 500);
  };

  const canComplete = bookType === 'fiction' 
    ? Object.values(fictionData).some(value => value.trim() !== '')
    : nonfictionChapters.some(chapter => 
        Object.entries(chapter).some(([key, value]) => 
          key !== 'chapter' && value.trim() !== ''
        )
      );

  const acts = [
    { name: 'Act 1: Setup', purpose: 'Introduce character, world, and inciting incident' },
    { name: 'Act 2A: Rising Action', purpose: 'Develop conflict and obstacles' },
    { name: 'Act 2B: Midpoint Crisis', purpose: 'Major revelation or setback' },
    { name: 'Act 3: Resolution', purpose: 'Climax and resolution' }
  ];

  return (
    <ModuleLayout
      title="Structure the Story or Message"
      description="Create a clear roadmap for your book — fiction or nonfiction — so momentum replaces overwhelm."
      icon={FiLayers}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Book Type Selector */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Book Type</h3>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="fiction"
                checked={bookType === 'fiction'}
                onChange={(e) => setBookType(e.target.value)}
                className="mr-2"
              />
              <span className="text-gray-700">Fiction</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="nonfiction"
                checked={bookType === 'nonfiction'}
                onChange={(e) => setBookType(e.target.value)}
                className="mr-2"
              />
              <span className="text-gray-700">Nonfiction</span>
            </label>
          </div>
        </div>

        {/* Fiction Path */}
        {bookType === 'fiction' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">StoryWise 4-Act Structure</h3>
              <p className="text-gray-600">Structure your story using proven dramatic principles.</p>
            </div>

            {acts.map((act, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{act.name}</h4>
                <p className="text-sm text-gray-600 mb-4">{act.purpose}</p>
                <textarea
                  value={fictionData[`act${index + 1}`] || ''}
                  onChange={(e) => handleFictionChange(`act${index + 1}`, e.target.value)}
                  rows={4}
                  className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="What happens in this act?"
                />
              </div>
            ))}

            <div className="bg-white rounded-xl shadow-sm p-6">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Additional Notes
              </label>
              <textarea
                value={fictionData.notes}
                onChange={(e) => handleFictionChange('notes', e.target.value)}
                rows={4}
                className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Any additional thoughts about your story structure..."
              />
            </div>
          </div>
        )}

        {/* Nonfiction Path */}
        {bookType === 'nonfiction' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Chapter Map</h3>
              <p className="text-gray-600">Plan each chapter with clear biblical grounding and reader transformation.</p>
            </div>

            {nonfictionChapters.map((chapter, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Chapter {chapter.chapter}
                  </h4>
                  {nonfictionChapters.length > 1 && (
                    <button
                      onClick={() => removeChapter(index)}
                      className="text-red-500 hover:text-red-700 p-2"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Big Idea
                    </label>
                    <input
                      type="text"
                      value={chapter.bigIdea}
                      onChange={(e) => handleChapterChange(index, 'bigIdea', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Main concept for this chapter"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Biblical Truth
                    </label>
                    <input
                      type="text"
                      value={chapter.biblicalTruth}
                      onChange={(e) => handleChapterChange(index, 'biblicalTruth', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Scripture or principle"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reader Shift
                    </label>
                    <input
                      type="text"
                      value={chapter.readerShift}
                      onChange={(e) => handleChapterChange(index, 'readerShift', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="How will they change?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Key Takeaway
                    </label>
                    <input
                      type="text"
                      value={chapter.keyTakeaway}
                      onChange={(e) => handleChapterChange(index, 'keyTakeaway', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Main action or insight"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addChapter}
              className="w-full bg-gray-50 hover:bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl p-6 flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <FiPlus />
              <span>Add Chapter</span>
            </button>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default ModuleTwo;